import React, { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  OPTIMIZATION_CORE_WEB_VITALS, 
  OPTIMIZATION_PROCESS, 
  OPTIMIZATION_PLANS,
  OPTIMIZATION_BENEFITS,
  OPTIMIZATION_TESTIMONIAL
} from "../mock/mock";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { 
  Shield, 
  Zap, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Smartphone,
  Gauge,
  Target,
  BarChart3,
  Phone,
  Mail,
  Globe,
  MessageCircle
} from "lucide-react";
import { WHATSAPP_LINK } from "../config/contact";
import { submitLead } from "../lib/lead";
import SEO from "../components/SEO";
import Header from "../components/Header";
const urlPattern = /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

// Schema para formulario de contacto
const schema = z.object({
  nombre: z.string().min(2, "Ingresa tu nombre"),
  email: z.string().email("Correo inválido"),
  telefono: z.string().min(7, "Teléfono inválido"),
  url: z.string().trim().optional().refine((value) => !value || urlPattern.test(value), "URL inválida"),
  mensaje: z.string().min(5, "Cuéntanos brevemente tu caso"),
  plan: z.string(),
  urgencia: z.string(),
  canal: z.string(),
  consentimiento: z.boolean().refine(v => v, { message: "Requerimos tu consentimiento" })
});

const BenefitIcon = ({ name }) => {
  const map = { TrendingUp, DollarSign, Users, Smartphone };
  const Icon = map[name] || Zap;
  return <Icon className="w-8 h-8 text-[#6f9533]" />;
};

export default function Optimizacion() {
  const { toast } = useToast();
  const formRef = useRef(null);
  const plansRef = useRef(null);
  
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: "",
      email: "",
      telefono: "",
      url: "",
      mensaje: "",
      plan: "optimizacion",
      urgencia: "consulta",
      canal: "Email",
      consentimiento: false
    }
  });
  const consentimiento = watch("consentimiento");
  const openWhatsApp = () => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (data) => {
    const result = await submitLead(data, "optimizacion-page");
    if (result.delivered) {
      toast({ 
        title: "¡Solicitud enviada!", 
        description: "Te contactaremos en breve por tu canal preferido.", 
      });
      formRef.current?.reset?.();
    } else {
      toast({ 
        title: "No pudimos enviar tu solicitud", 
        description: "Por favor contáctanos por WhatsApp para atención inmediata.", 
      });
    }
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      <SEO 
        title="Optimización de Velocidad WordPress | Core Web Vitals"
        description="Optimización técnica profunda para WordPress de GAREY AW. Mejora PageSpeed, Core Web Vitals y SEO. Resultados medibles garantizados. Desde $299 USD."
        keywords="optimización wordpress, velocidad wordpress, core web vitals, pagespeed wordpress, optimizar wordpress, acelerar wordpress, seo wordpress"
        canonicalUrl="https://wprescate24.com/optimizacion-velocidad-wordpress"
      />
      <div className="dark min-h-screen bg-background">
      <Header 
        currentPage="optimizacion"
        internalLinks={[
          { href: "#metricas", label: "Métricas" },
          { href: "#proceso", label: "Proceso" },
          { href: "#planes", label: "Planes" }
        ]}
        ctaText="Solicitar"
        ctaHref="#contacto"
        ctaColor="orange"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#6f9533]/5 via-background to-primary/5">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6f9533]/10 border border-[#6f9533]/30 mb-6">
            <Gauge className="w-4 h-4 text-[#6f9533]" />
            <span className="text-sm font-medium text-[#6f9533]">Optimización Técnica Profunda</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight max-w-4xl mx-auto">
            Optimización Técnica Profunda para <span className="text-[#6f9533]">WordPress</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed">
            Acelera tu sitio y mejora tu <strong>SEO</strong>. Optimización basada en <strong>datos reales</strong> y mejora de <strong>Core Web Vitals</strong> para máximo rendimiento.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <Button className="bg-[#6f9533] hover:bg-[#5a7a2a] text-white font-bold px-8 py-6 text-base" asChild>
              <a href="#contacto">🚀 Solicitar Optimización</a>
            </Button>
            <Button variant="outline" className="border-[#6f9533] text-[#6f9533] hover:bg-[#6f9533]/5 font-semibold px-8 py-6 text-base" onClick={openWhatsApp}>
              Hablar por WhatsApp
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-sm text-foreground/70">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#6f9533]" /> 
              <strong>Mejora medible</strong>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#6f9533]" /> 
              <strong>Basado en datos</strong>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#6f9533]" /> 
              <strong>Resultados en SEO</strong>
            </div>
          </div>
        </div>
      </section>
      {/* Quick Contact Strip */}
      <section className="py-5 bg-[#6f9533] text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm font-semibold">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Respuesta inicial en minutos</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>Diagnóstico técnico sin costo</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Sin compromiso</span>
            </div>
          </div>
          <Button className="bg-white text-[#6f9533] hover:bg-white/90 font-bold" onClick={openWhatsApp}>
            <MessageCircle className="w-4 h-4 mr-2" />
            Quiero atención por WhatsApp
          </Button>
        </div>
      </section>

      {/* Core Web Vitals */}
      <section id="metricas" className="py-20 bg-background border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Métricas que Optimizamos (Core Web Vitals)</h2>
            <p className="mt-4 text-lg text-foreground/70">
              Google usa estas métricas para evaluar la experiencia de usuario y posicionamiento SEO
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {OPTIMIZATION_CORE_WEB_VITALS.map((vital) => (
              <Card key={vital.metric} className="bg-gradient-to-br from-[#6f9533]/5 to-primary/5 border-2 border-border hover:border-[#6f9533]/50 transition-all hover:shadow-xl">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-16 w-16 rounded-2xl bg-[#6f9533]/10 flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#6f9533]">{vital.metric}</span>
                    </div>
                    <Target className="w-8 h-8 text-[#6f9533]/40" />
                  </div>
                  <CardTitle className="text-foreground text-lg font-bold">{vital.name}</CardTitle>
                  <p className="text-sm text-foreground/60 mt-1">{vital.desc}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground/60">Objetivo:</span>
                      <span className="font-bold text-[#6f9533]">{vital.target}</span>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-foreground/70"><strong>Impacto:</strong> {vital.impact}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-b from-background to-[#6f9533]/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">¿Por qué optimizar la velocidad?</h2>
            <p className="mt-4 text-lg text-foreground/70">Resultados reales respaldados por datos</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {OPTIMIZATION_BENEFITS.map((benefit) => (
              <Card key={benefit.title} className="bg-background border-2 border-border hover:border-[#6f9533]/50 transition-all hover:shadow-xl text-center">
                <CardHeader>
                  <div className="h-16 w-16 rounded-2xl bg-[#6f9533]/10 flex items-center justify-center mb-4 mx-auto">
                    <BenefitIcon name={benefit.icon} />
                  </div>
                  <CardTitle className="text-foreground text-lg font-bold">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/70 text-sm mb-4">{benefit.desc}</p>
                  <div className="inline-block px-3 py-1 rounded-full bg-[#6f9533]/10 border border-[#6f9533]/30">
                    <span className="text-[#6f9533] font-bold text-sm">{benefit.stat}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso Técnico */}
      <section id="proceso" className="py-20 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Nuestro Proceso Técnico</h2>
            <p className="text-lg text-foreground/70 mt-4">Metodología probada en cientos de sitios WordPress</p>
          </div>
          <div className="grid md:grid-cols-5 gap-6 mt-12">
            {OPTIMIZATION_PROCESS.map((step) => (
              <Card key={step.step} className="bg-gradient-to-br from-[#6f9533]/5 to-primary/5 border-2 border-border hover:border-[#6f9533]/50 hover:shadow-xl transition-all relative">
                <CardHeader>
                  <div className="absolute -top-4 left-6 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#6f9533] text-white text-lg font-bold border-4 border-background">
                    {step.step}
                  </div>
                  <div className="pt-4">
                    <CardTitle className="text-foreground text-base font-bold mb-2">{step.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="text-foreground/70 text-xs leading-relaxed">
                  {step.desc}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Planes de Optimización */}
      <section id="planes" ref={plansRef} className="py-20 bg-gradient-to-b from-background to-[#6f9533]/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Planes de Optimización</h2>
            <p className="text-lg text-foreground/70 mt-4">Elige el nivel de optimización que necesitas</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-12">
            {OPTIMIZATION_PLANS.map((plan) => (
              <Card key={plan.id} className={`relative bg-background border-2 ${plan.highlight ? "border-[#6f9533] shadow-xl shadow-[#6f9533]/10 scale-105" : "border-border"} overflow-hidden transition-all hover:shadow-2xl`}>
                {plan.highlight && (
                  <div className="absolute right-4 top-4 text-xs px-3 py-1 rounded-full bg-[#6f9533] text-white font-bold">Recomendado</div>
                )}
                <CardHeader className="space-y-4">
                  <CardTitle className="text-foreground">
                    <div className="text-2xl font-bold">{plan.name}</div>
                    <div className="flex items-baseline gap-2 mt-3">
                      <span className="text-4xl font-bold text-[#6f9533]">${plan.price}</span>
                      <span className="text-foreground/60 text-sm">{plan.priceUSD ? 'USD' : 'MXN'} {plan.period}</span>
                    </div>
                  </CardTitle>
                  <p className="text-sm text-foreground/70">{plan.description}</p>
                  <div className="inline-block px-3 py-2 rounded-lg bg-[#6f9533]/10 border border-[#6f9533]/30">
                    <p className="text-xs text-[#6f9533] font-semibold">{plan.results}</p>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-foreground/80">
                        <CheckCircle2 className="w-5 h-5 text-[#6f9533] mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button className={`w-full font-bold ${plan.highlight ? 'bg-[#6f9533] hover:bg-[#5a7a2a]' : 'bg-primary hover:bg-primary/90'} text-white`} asChild>
                    <a href="#contacto">
                      Solicitar Optimización <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonio */}
      <section className="py-20 bg-gradient-to-br from-[#6f9533]/10 via-background to-primary/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Caso de Éxito</h2>
            <p className="text-lg text-foreground/70 mt-4">{OPTIMIZATION_TESTIMONIAL.highlight}</p>
          </div>
          <Card className="bg-background border-2 border-[#6f9533]/30 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <blockquote className="text-lg text-foreground/90 italic leading-relaxed mb-8">
                "{OPTIMIZATION_TESTIMONIAL.quote}"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-[#6f9533]/10 border-2 border-[#6f9533]/30 text-[#6f9533] flex items-center justify-center text-xl font-bold">
                  {OPTIMIZATION_TESTIMONIAL.initials}
                </div>
                <div>
                  <div className="text-foreground text-lg font-bold">{OPTIMIZATION_TESTIMONIAL.name}</div>
                  <div className="text-foreground/60">{OPTIMIZATION_TESTIMONIAL.role}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Formulario de Contacto */}
      <section id="contacto" className="py-20 bg-gradient-to-b from-background to-[#6f9533]/5 border-t border-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">¿Listo para acelerar tu WordPress?</h2>
            <p className="text-lg text-foreground/70 mt-4">Cuéntanos sobre tu sitio y te contactaremos con un plan de optimización</p>
          </div>
          <div ref={formRef} className="rounded-xl bg-gradient-to-br from-[#6f9533]/5 to-primary/5 border-2 border-border p-8">
            <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="col-span-1">
                <Label htmlFor="nombre" className="text-foreground font-medium">Nombre</Label>
                <Input id="nombre" placeholder="Tu nombre" {...register("nombre")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.nombre && <p className="text-destructive text-xs mt-1">{errors.nombre.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
                <Input id="email" placeholder="tucorreo@ejemplo.com" {...register("email")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="telefono" className="text-foreground font-medium">Teléfono</Label>
                <Input id="telefono" placeholder="+52 722 381 8617" {...register("telefono")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.telefono && <p className="text-destructive text-xs mt-1">{errors.telefono.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="url" className="text-foreground font-medium">URL del sitio (opcional)</Label>
                <Input id="url" placeholder="tusitio.com (opcional)" {...register("url")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.url && <p className="text-destructive text-xs mt-1">{errors.url.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">Servicio de interés</Label>
                <Select onValueChange={(v) => setValue("plan", v)} defaultValue="optimizacion">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Elige un servicio" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="rescate">Rescate</SelectItem>
                    <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                    <SelectItem value="optimizacion">Optimización</SelectItem>
                    <SelectItem value="hosting">Hosting Profesional</SelectItem>
                  </SelectContent>
                </Select>
                {errors.plan && <p className="text-destructive text-xs mt-1">{errors.plan.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">Canal preferido</Label>
                <Select onValueChange={(v) => setValue("canal", v)} defaultValue="Email">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="¿Cómo te contactamos?" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                    <SelectItem value="Llamada">Llamada</SelectItem>
                  </SelectContent>
                </Select>
                {errors.canal && <p className="text-destructive text-xs mt-1">{errors.canal.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1">
                <Label htmlFor="mensaje" className="text-foreground font-medium">Cuéntanos sobre tu sitio</Label>
                <Textarea id="mensaje" rows={4} placeholder="Ej: Tengo un blog con 10,000 visitas mensuales, PageSpeed de 45..." {...register("mensaje")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.mensaje && <p className="text-destructive text-xs mt-1">{errors.mensaje.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1 flex items-center gap-2">
                <Checkbox
                  id="consentimiento"
                  checked={consentimiento}
                  onCheckedChange={(checked) => setValue("consentimiento", checked)}
                />
                <Label htmlFor="consentimiento" className="text-sm text-foreground/70">Acepto ser contactado y la política de privacidad.</Label>
              </div>
              {errors.consentimiento && <p className="md:col-span-2 col-span-1 text-destructive text-xs -mt-2">{errors.consentimiento.message}</p>}
              <div className="md:col-span-2 col-span-1">
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#6f9533] hover:bg-[#5a7a2a] text-white font-bold py-6">
                  {isSubmitting ? "Enviando..." : "Solicitar Optimización"}
                </Button>
                <p className="mt-3 text-xs text-foreground/70 text-center">Normalmente respondemos en menos de 15 minutos por WhatsApp o correo.</p>
              </div>
            </form>
            <div className="mt-4 text-xs text-foreground/60 flex items-center gap-4">
              <div className="flex items-center gap-1"><Phone className="w-4 h-4" /> Respuesta rápida</div>
              <div className="flex items-center gap-1"><Mail className="w-4 h-4" /> Sin compromiso</div>
              <div className="flex items-center gap-1"><Globe className="w-4 h-4" /> Auditoría incluida</div>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-foreground/60 mb-4">¿Prefieres hablar directamente?</p>
            <Button className="bg-[#6f9533] hover:bg-[#5a7a2a] text-white font-bold px-8 py-4" asChild>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                <MessageCircle className="w-5 h-5 mr-2" />
                Contactar por WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-[#b8cd37]" />
                <span className="font-bold text-white text-lg">WPRescate24</span>
              </div>
              <p className="text-sm text-white/60">
                Especialistas en optimización y rescate de sitios WordPress.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-3">Servicios</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li><RouterLink to="/" className="hover:text-white">Rescate de Emergencia</RouterLink></li>
                <li><RouterLink to="/mantenimiento-wordpress" className="hover:text-white">Mantenimiento</RouterLink></li>
                <li><RouterLink to="/optimizacion-velocidad-wordpress" className="hover:text-white">Optimización</RouterLink></li>
                <li><RouterLink to="/hosting-wordpress" className="hover:text-white">Hosting WordPress</RouterLink></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-3">Empresa</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#contacto" className="hover:text-white">Contacto</a></li>
                <li><a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-white">WhatsApp</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-3">Recursos</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="https://servidores.garey.mx" target="_blank" rel="noopener" className="hover:text-white">Servidores</a></li>
                <li><RouterLink to="/blog" className="hover:text-white">Blog</RouterLink></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/15 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between text-sm text-white/60">
            <div>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-white hover:underline font-semibold">GAREY AW</a></div>
            <div className="flex items-center gap-6 mt-3 md:mt-0">
              <RouterLink to="/terminos" className="hover:text-white transition-colors">Términos</RouterLink>
              <RouterLink to="/privacidad" className="hover:text-white transition-colors">Privacidad</RouterLink>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-[#6f9533] transition-colors">WhatsApp</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener" aria-label="Abrir WhatsApp" className="fixed bottom-6 right-6 z-50 group">
        <div className="h-16 w-16 rounded-full bg-[#6f9533] hover:bg-[#5a7a2a] flex items-center justify-center shadow-2xl border-2 border-[#6f9533]/50 transition-all hover:scale-110">
          <MessageCircle className="w-8 h-8 text-white" />
        </div>
      </a>
      </div>
    </>
  );
}
